import { Link, useParams } from "react-router-dom";
import styles from "./Product.module.css";
import { useProduct } from "./useAddEditProduct";
import Spinner from "../../Spinner/Spinner";
import { ImFolderDownload } from "react-icons/im";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { InformationFields, DetailsFields, PricingFields } from "./constant";
import { ProductFieldInput } from "./Input";
import { IoMdAdd } from "react-icons/io";
import { VariantCard } from "./VariantCard";
import { MdOutlineAutoFixHigh, MdOutlineChangeCircle } from "react-icons/md";
import { FaPencilAlt } from "react-icons/fa";
import { useState } from "react";
import { CropImageDialog } from "./CropImage";
import { ChangeImageDialog } from "./ChangeImage";
import { removeImageBackground } from "../../../_helpers/image";

export const AddEditProduct = () => {
  const [showChangeImage, setShowChangeImage] = useState(false);
  const { id, catalogId } = useParams();
  const [showCrop, setShowCrop] = useState(false);
  const {
    product,
    productDetailsLoading,
    handleSubmit,
    handleDelete,
    handleChange,
    handleRemoveBg,
    handleSelectImage,
    removeBgLoading,
    selectedImageIndex,
    setProduct,
    handleRemoveImage,
    isSaving,
  } = useProduct();

  const handleVariantFieldChange = (index, field, value) => {
    handleChange(`variants.${index}.${field}`, value);
  };

  const handleVariantDelete = (index) => {
    const updatedVariants = [...(product?.variants || [])];
    updatedVariants.splice(index, 1);
    handleChange("variants", updatedVariants);
  };

  const handleAddVariant = () => {
    const updatedVariants = [
      ...(product?.variants || []),
      {
        type: "",
        varName: "",
        sku: "",
        images: [],
        hexColor: "#000000",
      },
    ];
    handleChange("variants", updatedVariants);
  };

  const imageSlots = [0, 1];

  if (productDetailsLoading) {
    return <Spinner />;
  }
  return (
    <div className={styles.productContainer}>
      <div className={styles.formContainer}>
        <div className={styles.upper}>
          <Link to="/">
            <span className={styles.swag}>
              Website
              <span className={styles.greater}> &gt; </span>
            </span>
          </Link>
          <Link to="/catalogs">
            <span className={styles.swag}>
              Catalogues
              <span className={styles.greater}> &gt; </span>
            </span>
          </Link>
          <Link to={`/catalogs/${catalogId}`}>
            <span className={styles.swag}>
              Products Listing
              <span className={styles.greater}> &gt; </span>
            </span>
          </Link>
          <span className={styles.req}>{product.name ?? "New Product"}</span>
        </div>
        <div className={styles.headingContainer}>
          <div className={styles.heading}>{product.name ?? "New Product"}</div>
          <div className={styles.buttonContainer}>
            <button
              className={styles.button}
              onClick={() => handleSubmit(product)}
            >
              <ImFolderDownload />
              {isSaving ? "Saving..." : "Save"}
            </button>
            {id ? (
              <button className={styles.deleteButton} onClick={handleDelete}>
                <RiDeleteBin5Fill />
                Delete
              </button>
            ) : null}
          </div>
        </div>
        {!!id && <div className={styles.caption}> ID: {id}</div>}
        <div className={styles.imageList}>
          {imageSlots.map((slotIndex) => (
            <ImageCard
              key={slotIndex}
              image={product?.images?.[slotIndex]}
              isSelected={selectedImageIndex === slotIndex}
              onClick={() => handleSelectImage(slotIndex)}
              handleRemoveBg={() => handleRemoveBg(slotIndex)}
              removeBgLoading={removeBgLoading}
            />
          ))}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            justifyContent: "center",
          }}
        >
          <button
            className={styles.deleteButton}
            style={{ justifyItems: "center", alignSelf: "center" }}
            onClick={() => handleRemoveImage(selectedImageIndex)}
          >
            <RiDeleteBin5Fill />
            Delete Selected Image
          </button>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignSelf: "center",
              justifyContent: "center",
            }}
          >
            <button
              className={styles.button}
              disabled={removeBgLoading || selectedImageIndex === null}
              onClick={() => setShowChangeImage(true)}
            >
              <MdOutlineChangeCircle />
              Change Image
            </button>
            <button
              className={styles.button}
              onClick={() => setShowCrop(true)}
              disabled={removeBgLoading || selectedImageIndex === null}
            >
              <FaPencilAlt />
              Edit Image
            </button>
          </div>
        </div>
        <div className={styles.formHeading}>Product Information</div>
        {InformationFields.map((field) => (
          <ProductFieldInput
            key={field.value}
            label={field.label}
            name={field.value}
            type={field.type}
            value={product?.[field.value]}
            placeholder={field.placeholder}
            required={field.required}
            onChange={(nextValue) => handleChange(field.value, nextValue)}
            stacked={false}
            options={field.options ?? []}
          />
        ))}
        <div className={styles.sectionHeading}>Product Details</div>
        {DetailsFields.map((field) => (
          <ProductFieldInput
            key={field.value}
            label={field.label}
            name={field.value}
            type={field.type}
            value={product?.[field.value]}
            placeholder={field.placeholder}
            required={field.required}
            onChange={(nextValue) => handleChange(field.value, nextValue)}
            stacked
          />
        ))}
        <div className={styles.sectionHeading}>Pricing</div>
        {PricingFields.map((field) => (
          <ProductFieldInput
            key={field.value}
            label={field.label}
            name={field.value}
            type={field.type}
            value={product?.[field.value]}
            placeholder={field.placeholder}
            required={field.required}
            suffix={field.suffix}
            disabled={field.disabled}
            onChange={(nextValue) => handleChange(field.value, nextValue)}
            stacked
          />
        ))}
        <div className={styles.sectionHeading}>Variants</div>
        <button className={styles.addVariantButton} onClick={handleAddVariant}>
          <IoMdAdd />
          Add Variant
        </button>
        <div className={styles.variantList}>
          {(product?.variants || []).length === 0 ? (
            <div className={styles.emptyState}>No variants added yet.</div>
          ) : (
            product.variants.map((variant, index) => (
              <VariantCard
                key={variant._id || index}
                variant={variant}
                index={index}
                onFieldChange={handleVariantFieldChange}
                onDelete={handleVariantDelete}
                handleRemoveBg={async (index) => {
                  const res = await removeImageBackground(
                    variant.images[index]
                  );
                  if (res) {
                    const imageUrl = URL.createObjectURL(res);
                    handleChange(`variants.${index}.images`, [imageUrl]);
                  }
                }}
              />
            ))
          )}
        </div>
      </div>
      <>
        <CropImageDialog
          show={showCrop}
          switch={() => setShowCrop(false)}
          image={product?.images[selectedImageIndex]}
          onCropComplete={(blob) => {
            const updatedImages = [...(product?.images || [])];
            updatedImages[selectedImageIndex] = URL.createObjectURL(blob);
            handleChange("images", updatedImages);
          }}
        />
        <ChangeImageDialog
          show={showChangeImage}
          switch={() => setShowChangeImage(false)}
          handleSave={(image) => {
            const updatedImages = [...(product?.images || [])];
            updatedImages[selectedImageIndex] = image;
            handleChange("images", updatedImages);
            setShowChangeImage(false);
          }}
        />
      </>
    </div>
  );
};

const ImageCard = ({
  image,
  isSelected,
  onClick,
  handleRemoveBg,
  removeBgLoading,
}) => {
  return (
    <div
      className={styles.imageContainer}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick();
      }}
      style={isSelected ? { border: "2px solid black" } : {}}
    >
      {image ? (
        <>
          <img src={image} alt="image" className={styles.image} />
          <button
            className={styles.removeBgButton}
            onClick={() => handleRemoveBg()}
            disabled={removeBgLoading}
          >
            <MdOutlineAutoFixHigh />
            {removeBgLoading ? "Processing..." : "Remove Background"}
          </button>
        </>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
          }}
        >
          Add Image
        </div>
      )}
    </div>
  );
};
